// No-search-results empty state — Stream 3 PR-5 (2026-05-15),
// rebuilt 2026-07-02 (UI/UX audit P1): the old version blamed geography
// ("we're not fully launched in your area yet") for EVERY zero-result
// state — keyword misses, price misses with no location set — and its
// recovery chips were dead code (they read/wrote pub_categoryLevel1
// after the search had moved to pub_occasions).
//
// The message now names the actual cause, recovery actions render ABOVE
// the waitlist form, and the waitlist only shows when it makes sense
// (occasion/area misses — genuine demand signals), not for a typo'd
// keyword.

import React from 'react';

import { FormattedMessage } from '../../../util/reactIntl';
import { parse, stringify } from '../../../util/urlHelpers';
import {
  getCategoryEmoji,
  getAdjacentOccasions,
  cityFromSearchAddress,
} from '../../../util/listingHelpers';
import { occasionLabel, occasionKeyFromQueryValue } from '../../../data/occasions';

import WaitlistForm, { extractZipFromAddress } from './WaitlistForm';
import css from './NoSearchResultsMaybe.module.css';

const NoSearchResultsMaybe = props => {
  const { listingsAreLoaded, totalItems, location, resetAll } = props;
  const hasNoResult = listingsAreLoaded && totalItems === 0;
  const hasSearchParams = location.search?.length > 0;

  if (!hasNoResult) return null;

  const search = parse(location.search);
  const initialZip = extractZipFromAddress(search.address);

  // WHY are there no results? Priority: keyword > occasion > other
  // filters > area. The canonical occasion param is pub_occasions
  // (possibly has_any:-prefixed); the legacy params are read for
  // back-compat with old inbound links.
  const keyword =
    typeof search.keywords === 'string' && search.keywords.trim()
      ? search.keywords.trim()
      : null;
  const occasionKey = occasionKeyFromQueryValue(
    search.pub_occasions || search.pub_categoryLevel1 || search.categoryLevel1
  );
  const cityLabel = cityFromSearchAddress(search.address);
  const hasArea = !!search.address || !!search.bounds;
  const otherFilterLabels = [
    search.price ? 'price' : null,
    search.dates ? 'dates' : null,
    search.pub_fulfillment ? 'pickup/delivery' : null,
  ].filter(Boolean);

  const cause = keyword
    ? 'keyword'
    : occasionKey
    ? 'occasion'
    : otherFilterLabels.length > 0
    ? 'filters'
    : hasArea
    ? 'area'
    : 'none';

  // Adjacent occasions — preserve the rest of the search (city etc.) so
  // a chip lands on "<other occasion> kits in <same city>". Writes the
  // canonical pub_occasions param (the old pub_categoryLevel1 write
  // matched nothing, so these chips never worked).
  const adjacent = cause === 'occasion' ? getAdjacentOccasions(occasionKey) : [];
  const adjacentHref = key => {
    const params = { ...search, pub_occasions: key };
    delete params.pub_categoryLevel1;
    delete params.categoryLevel1;
    delete params.page;
    return `/s?${stringify(params)}`;
  };

  const clearKeywordHref = (() => {
    const params = { ...search };
    delete params.keywords;
    delete params.page;
    return `/s?${stringify(params)}`;
  })();

  const occasionName = occasionKey ? occasionLabel(occasionKey).toLowerCase() : null;

  const title =
    cause === 'keyword' ? (
      <>Nothing matches &ldquo;{keyword}&rdquo;</>
    ) : cause === 'occasion' && cityLabel ? (
      <>
        No {occasionName} kits in {cityLabel} yet
      </>
    ) : cause === 'occasion' ? (
      <>No {occasionName} kits yet</>
    ) : cause === 'filters' ? (
      <>No kits match these filters</>
    ) : (
      <FormattedMessage id="NoSearchResults.headline" />
    );

  const description =
    cause === 'keyword' ? (
      <>Try a different word, or clear the search and browse everything.</>
    ) : cause === 'occasion' ? (
      <>Be the first to know when one lists{cityLabel ? ` in ${cityLabel}` : ''}.</>
    ) : cause === 'filters' ? (
      <>
        Try loosening the {otherFilterLabels.join(' and ')} filter
        {otherFilterLabels.length > 1 ? 's' : ''}.
      </>
    ) : (
      <FormattedMessage id="NoSearchResults.subhead" />
    );

  const showWaitlist = cause === 'occasion' || cause === 'area' || cause === 'none';

  return (
    <div className={css.noSearchResults}>
      <div className={css.emptyStateIcon} aria-hidden="true">
        {occasionKey ? getCategoryEmoji(occasionKey) : keyword ? '🔍' : '🎈'}
      </div>

      <h2 className={css.emptyStateTitle}>{title}</h2>

      <p className={css.emptyStateDescription}>{description}</p>

      {/* Recovery first — it used to hide a full screen below the form. */}
      <div className={css.recoveryRow}>
        {cause === 'keyword' ? (
          <a className={css.adjacentChip} href={clearKeywordHref}>
            Clear &ldquo;{keyword}&rdquo; <span aria-hidden="true">&times;</span>
          </a>
        ) : null}
        {hasSearchParams ? (
          <button className={css.resetAllFiltersButton} onClick={e => resetAll(e)}>
            <FormattedMessage id="NoSearchResults.clearFilters" />
          </button>
        ) : null}
      </div>

      {adjacent.length > 0 && (
        <div className={css.adjacentOccasions}>
          <h3 className={css.adjacentTitle}>Browse nearby occasions</h3>
          <div className={css.adjacentChips}>
            {adjacent.map(key => (
              <a key={key} className={css.adjacentChip} href={adjacentHref(key)}>
                <span aria-hidden="true">{getCategoryEmoji(key)}</span>
                <span>{occasionLabel(key)}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {showWaitlist ? (
        <div className={css.formWrapper}>
          <WaitlistForm initialZip={initialZip} source="empty_search_results" />
        </div>
      ) : null}
    </div>
  );
};

export default NoSearchResultsMaybe;
